// Collection and search listing: filter and sort changes fetch the section through the Section Rendering API and
// morph the new results, filters and counts into place (Horizon's morph keeps focus, open filter groups and the open
// drawer), keeping the URL in step so Back/Forward and shared links work. Only the latest request counts; if one
// fails the browser simply loads the page. Without script the filters and sort are a plain GET form and links.
import { morph } from '@theme/morph';
import { openDialog, setupDialog } from '@purolabs/dialog';

class Collection extends HTMLElement {
  #events;
  #request;
  #url = location.href;

  connectedCallback() {
    this.#events = new AbortController();
    const { signal } = this.#events;
    this.dialog = this.querySelector('.pl-filters');
    setupDialog(this.dialog, signal);

    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-open-filters]');
      if (opener && this.dialog) openDialog(this.dialog, opener);
      if (event.target.closest('[data-close-filters]')) this.dialog?.close();
      const link = event.target.closest('[data-filter-link]');
      if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
        event.preventDefault();
        link.closest('details')?.removeAttribute('open');
        this.#load(link.href);
      }
    }, { signal });

    this.addEventListener('change', (event) => {
      const form = event.target.form;
      if (form?.matches('[data-filters-form]')) this.#submit(form);
    }, { signal });

    // The filters form is submitted by script on each change; Enter in a price box lands here
    this.addEventListener('submit', (event) => {
      if (!event.target.matches('[data-filters-form]')) return;
      event.preventDefault();
      this.#submit(event.target);
    }, { signal });

    // Close the sort list when clicking elsewhere or pressing Escape
    document.addEventListener('click', (event) => {
      const sort = this.querySelector('.pl-sort[open]');
      if (sort && !sort.contains(event.target)) sort.removeAttribute('open');
    }, { signal });
    this.addEventListener('keydown', (event) => {
      const sort = event.key === 'Escape' && event.target.closest('.pl-sort[open]');
      if (!sort) return;
      sort.removeAttribute('open');
      sort.querySelector('summary').focus();
    }, { signal });

    window.addEventListener('popstate', () => {
      // Hash-only changes keep the same results
      if (stripHash(location.href) === stripHash(this.#url)) return;
      this.#load(location.href, { push: false });
    }, { signal });
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.#request?.abort();
  }

  #submit(form) {
    const params = new URLSearchParams(new FormData(form));
    // Empty price boxes would otherwise filter on nothing
    for (const [key, value] of [...params]) if (value === '') params.delete(key);
    this.#load(`${form.action}?${params}`);
  }

  // push: a new filter or sort starts again at page 1; Back/Forward (push false) restores the URL as it was, page included
  async #load(href, { push = true } = {}) {
    const url = new URL(href, location.href);
    if (push) url.searchParams.delete('page');
    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.dataset.sectionId);

    this.#request?.abort();
    const request = (this.#request = new AbortController());
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(fetchUrl, { signal: request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      if (request.signal.aborted) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('pl-collection');
      if (!fresh) throw new Error('No listing in the response');
      morph(this, fresh);
      this.#url = url.href;
      if (push) history.pushState({}, '', url);
      this.#announce();
    } catch (error) {
      if (request.signal.aborted) return;
      console.warn('Collection update failed, loading the page instead:', error);
      location.assign(url);
    } finally {
      if (this.#request === request) {
        this.#request = undefined;
        this.removeAttribute('aria-busy');
      }
    }
  }

  // Say how many products there are now; cleared first so the same count is read again
  #announce() {
    const status = this.querySelector('[data-results-status]');
    if (!status) return;
    status.textContent = '';
    setTimeout(() => (status.textContent = status.dataset.message), 100);
  }
}

const stripHash = (href) => href.split('#')[0];

if (!customElements.get('pl-collection')) customElements.define('pl-collection', Collection);
